// src/components/ChatDrawer.tsx
import { eventBus } from "@/utils/index";
import { CloseOutlined } from "@ant-design/icons";
import { useModel } from "@umijs/max";
import React, { useEffect } from "react";

const CHILD_ORIGIN = "https://admin-agora-chat-widget.pages.dev";

type Target = {
  chatType: "singleChat" | "groupChat";
  id: string;
  name?: string;
};

export type ChatDrawerRef = {
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const clamp = (min: number, v: number, max: number) =>
  Math.max(min, Math.min(v, max));

const ChatUIKitProvider = React.forwardRef(
  ({ right = 0, open, defaultOpen = false, onOpenChange }: any, ref) => {
    const { initialState, setInitialState }: any = useModel("@@initialState");
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const [unread, setUnread] = React.useState(0);

    // 受控/非受控合并
    const isControlled = open !== undefined;
    const [innerOpen, setInnerOpen] = React.useState(defaultOpen);
    const mergedOpen = isControlled ? (open as boolean) : innerOpen;
    const setMergedOpen = (next: boolean) => {
      if (!isControlled) setInnerOpen(next);
      onOpenChange?.(next);
    };

    // 尺寸
    const [size, setSize] = React.useState({ w: 766, h: 640 });
    useEffect(() => {
      const calc = () => {
        const vw = window.innerWidth,
          vh = window.innerHeight;
        const w = clamp(320, Math.min(766, vw - right - 16), 766);
        const h = clamp(400, Math.min(720, vh - 96), 720);
        setSize({ w, h });
      };
      calc();
      window.addEventListener("resize", calc);
      return () => window.removeEventListener("resize", calc);
    }, [right]);

    // 与子应用握手 + 未读
    const sendInit = React.useCallback(() => {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: "chat:init",
          payload: {
            appKey: initialState?.chatConfig?.appKey,
            userId: initialState?.chatConfig?.username,
            token: initialState?.chatConfig?.token,
            target: initialState?.chatConfig?.target,
          },
        },
        CHILD_ORIGIN
      );
    }, [initialState]);

    useEffect(() => {
      const onMsg = (e: MessageEvent) => {
        if (e.origin !== CHILD_ORIGIN) return;
        const { type, payload } = e.data || {};
        if (type === "chat:ready") sendInit();
        if (type === "chat:unread") setUnread(Number(payload?.total || 0));
      };
      window.addEventListener("message", onMsg);
      return () => window.removeEventListener("message", onMsg);
    }, [sendInit]);

    const postToChild = React.useCallback((type: string, payload?: any) => {
      iframeRef.current?.contentWindow?.postMessage(
        { type, payload },
        CHILD_ORIGIN
      );
    }, []);
    useEffect(() => {
      eventBus.on("gotoChat", (data: any) => {
        setMergedOpen(true);

        postToChild("chat:open", data); // 子应用已就绪：直接发
      });
      return () => eventBus.off("gotoChat");
    });

    // 暴露方法
    React.useImperativeHandle(
      ref,
      () => ({
        open: () => setMergedOpen(true),
        close: () => setMergedOpen(false),
        toggle: () => setMergedOpen(!mergedOpen),
      }),
      [mergedOpen]
    );

    return (
      <>
        {/* 右下角浮动按钮（内置切换；受控时也会回调 onOpenChange） */}
        {/* <FloatButton
          icon={
            <Badge count={unread} size="small" overflowCount={99}>
              <MessageOutlined />
            </Badge>
          }
          type="primary"
          onClick={() => setMergedOpen(!mergedOpen)}
          style={{ right, bottom: 24, zIndex: 1002 }}
        /> */}

        {/* 蒙层 */}
        <div
          onClick={() => setMergedOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(0,0,0,.35)",
            opacity: mergedOpen ? 1 : 0,
            pointerEvents: mergedOpen ? "auto" : "none",
            transition: "opacity .2s ease",
          }}
        />

        {/* 抽屉（iframe 常驻，不卸载） */}
        <div
          style={{
            position: "fixed",
            top: `0`,
            right,
            width: size.w,
            height: "100%",
            zIndex: 1001,
            background: "#fff",
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,

            overflow: "hidden",
            boxShadow: mergedOpen ? "0 18px 44px rgba(0,0,0,.24)" : "none",
            transform: mergedOpen
              ? "translateX(0)"
              : `translateX(${size.w + right + 8}px)`,
            opacity: mergedOpen ? 1 : 0,
            pointerEvents: mergedOpen ? "auto" : "none",
            transition:
              "transform .22s ease, opacity .22s ease, box-shadow .22s ease",
          }}
        >
          <div
            style={{
              background: "#f9fafa",
              padding: "5px 10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgb(238, 238, 238)",
            }}
          >
            <div
              style={{
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              Chat Messages
            </div>
            <button
              onClick={() => setMergedOpen(false)}
              aria-label="Close"
              style={{
                width: 32,
                height: 32,
                border: "none",
                cursor: "pointer",
                background: "rgba(0,0,0,.48)",
                color: "#fff",
                borderRadius: 16,
                display: "grid",
                placeItems: "center",
              }}
            >
              <CloseOutlined />
            </button>
          </div>

          <iframe
            ref={iframeRef}
            title="Chat"
            src={`${CHILD_ORIGIN}/`}
            style={{ width: "100%", height: "calc(100% - 43px)", border: 0 }}
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
        </div>
      </>
    );
  }
);

export default ChatUIKitProvider;
